import { IconArrowRight, IconBooks, IconMapPin, IconPhoneCall, IconSpeakerphone, IconUser, IconUsersGroup } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, numberScale, posterFields, solid, valueFitScale } from '../posterParts';

/**
 * "Calendar cards" — a soft, friendly infographic: a coloured header with a wavy edge and the logo sitting on it, two tear-off CALENDAR
 * tiles for the dates, a gradient vacancy card, two rounded icon cards and a pill call-to-action.
 * Palette: bg, header, headerDeep, cal1, cal2, vacancyA, vacancyB, c1, c2, ink, muted, cta, ctaDeep.
 */
const FONT = "'Baloo Da 2', 'Anek Bangla', 'Noto Sans Bengali', sans-serif";

function CalTile({ p, label, parts, color }) {
    return (
        <div className="relative flex flex-col items-center overflow-hidden text-center" style={{ width: fs(31), background: '#fff', borderRadius: fs(3.4), boxShadow: '0 0.9cqw 2cqw rgba(15,23,42,0.16)' }}>
            <div style={{ width: '100%', background: color, color: '#fff', fontSize: fs(2.8), fontWeight: 800, padding: `${fs(1.2)} 0 ${fs(1)}`, lineHeight: 1.15 }}>{label}</div>
            {/* binder rings */}
            <div className="absolute" style={{ left: '22%', top: fs(-0.6), width: fs(1.6), height: fs(3), background: '#fff', borderRadius: '99px', boxShadow: '0 0.2cqw 0.4cqw rgba(0,0,0,0.25)' }} />
            <div className="absolute" style={{ right: '22%', top: fs(-0.6), width: fs(1.6), height: fs(3), background: '#fff', borderRadius: '99px', boxShadow: '0 0.2cqw 0.4cqw rgba(0,0,0,0.25)' }} />
            <div style={{ color: p.ink, fontSize: fs(11), fontWeight: 800, lineHeight: 1, paddingTop: fs(1.6) }}>{parts.day}</div>
            <div style={{ color: p.muted, fontSize: fs(3.6), fontWeight: 700, lineHeight: 1.1, padding: `${fs(0.4)} 0 ${fs(2)}` }}>{parts.month}</div>
        </div>
    );
}

function IconCard({ p, Icon, label, color, children }) {
    return (
        <div style={{ background: '#fff', borderRadius: fs(3.4), padding: `${fs(2.4)} ${fs(2.6)} ${fs(2.6)}`, boxShadow: '0 0.9cqw 2cqw rgba(15,23,42,0.12)' }}>
            <div className="flex items-center" style={{ gap: fs(1.8), marginBottom: fs(1.4) }}>
                <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(7.6), height: fs(7.6), background: color }}>
                    <Icon style={{ width: fs(4.4), height: fs(4.4), color: '#fff' }} stroke={2.2} />
                </div>
                <div style={{ color: p.muted, fontSize: fs(2.9), fontWeight: 700, lineHeight: 1.15 }}>{label}</div>
            </div>
            {children}
        </div>
    );
}

export default function CalendarDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 26);
    const ageScale = valueFitScale(f.age, 20);

    const body = (
        <div className="flex flex-1 flex-col">
            {/* header */}
            <div className="relative text-center" style={{ background: `linear-gradient(180deg, ${p.header} 0%, ${p.headerDeep} 100%)`, padding: `${fs(5.2)} 6cqw ${fs(10)}` }}>
                <div className="inline-flex items-center" style={{ border: '0.35cqw solid rgba(255,255,255,0.85)', borderRadius: '99px', padding: `${fs(0.8)} ${fs(2.6)}`, gap: fs(1.2), color: '#fff', fontSize: fs(3), fontWeight: 800 }}>
                    <IconSpeakerphone style={{ width: fs(3.6), height: fs(3.6) }} stroke={2.2} />
                    <div>{f.label.notice}</div>
                </div>
                <div style={{ color: '#fff', fontSize: fs(6.6), fontWeight: 800, lineHeight: 1.14, marginTop: fs(2.4), textShadow: '0 0.3cqw 0.8cqw rgba(0,0,0,0.18)' }}>{f.org}</div>
                {f.position && <div style={{ color: '#fff', opacity: 0.92, fontSize: fs(3.7), fontWeight: 600, lineHeight: 1.25, marginTop: fs(1.2) }}>{f.position}</div>}
                <svg viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" style={{ position: 'absolute', left: 0, bottom: '-0.2cqw', width: '100%', height: '6.4cqw' }}>
                    <path d="M0 10 L0 4.4 C 18 -1.4, 34 9.4, 54 4.4 S 88 0.6, 100 4.2 L100 10 Z" fill={p.bg} />
                </svg>
            </div>

            <div className="flex flex-1 flex-col justify-center" style={{ padding: `0 6cqw ${fs(3)}`, gap: fs(2.8) }}>
                {/* the logo sits on the wave */}
                <div className="relative flex justify-center" style={{ marginTop: fs(-12), zIndex: 2 }}>
                    <PosterLogo job={job} size={19} style={{ background: '#fff', borderRadius: '50%', padding: fs(1.6), border: `${fs(0.8)} solid ${p.bg}`, boxShadow: '0 0.9cqw 2.2cqw rgba(15,23,42,0.22)' }} />
                </div>

                <div className="flex items-center justify-center" style={{ gap: fs(2.4) }}>
                    <CalTile p={p} label={f.label.start} parts={f.startParts} color={p.cal1} />
                    <IconArrowRight className="flex-none" style={{ width: fs(6), height: fs(6), color: p.muted, opacity: 0.8 }} stroke={2.6} />
                    <CalTile p={p} label={f.label.end} parts={f.endParts} color={p.cal2} />
                </div>

                <div className="relative flex items-center overflow-hidden" style={{ background: `linear-gradient(120deg, ${p.vacancyA}, ${p.vacancyB})`, borderRadius: fs(4), padding: `${fs(2.4)} ${fs(4)}`, gap: fs(3.4), color: '#fff', boxShadow: '0 1cqw 2.2cqw rgba(15,23,42,0.2)' }}>
                    <div className="pointer-events-none absolute rounded-full" style={{ right: fs(-6), top: fs(-8), width: fs(22), height: fs(22), background: 'rgba(255,255,255,0.12)' }} />
                    <div className="pointer-events-none absolute rounded-full" style={{ right: fs(8), bottom: fs(-9), width: fs(14), height: fs(14), background: 'rgba(255,255,255,0.1)' }} />
                    <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(13), height: fs(13), background: 'rgba(255,255,255,0.22)', border: `${fs(0.5)} solid rgba(255,255,255,0.7)` }}>
                        <IconUsersGroup style={{ width: fs(7.4), height: fs(7.4) }} stroke={2} />
                    </div>
                    <div className="relative">
                        <div style={{ fontSize: fs(3.4), fontWeight: 800, lineHeight: 1.1, opacity: 0.95 }}>{f.label.vacancy}</div>
                        <div className="flex items-baseline" style={{ gap: fs(1.4) }}>
                            <div style={{ fontSize: `calc(${11.5 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.05 }}>{f.vacancy}</div>
                            <div style={{ fontSize: fs(4.4), fontWeight: 800 }}>{f.label.unit}</div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2" style={{ gap: fs(2.6) }}>
                    <IconCard p={p} Icon={IconBooks} label={f.label.edu} color={p.c1}>
                        <div style={{ color: p.ink, fontSize: `calc(${3.7 * eduScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.22 }}>{f.edu}</div>
                    </IconCard>
                    <IconCard p={p} Icon={IconUser} label={f.label.age} color={p.c2}>
                        <div style={{ color: p.ink, fontSize: `calc(${3.7 * ageScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.22 }}>{f.age}</div>
                    </IconCard>
                </div>

                <div className="text-center" style={{ color: p.muted, fontSize: fs(2.8), fontWeight: 600 }}>✓ {f.label.promo}</div>

                <div className="flex items-center justify-center" style={{ background: `linear-gradient(90deg, ${p.cta}, ${p.ctaDeep})`, borderRadius: '99px', padding: `${fs(2)} ${fs(3)}`, gap: fs(2.4), color: '#fff', boxShadow: '0 1cqw 2.2cqw rgba(15,23,42,0.22)' }}>
                    <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(7.4), height: fs(7.4), background: 'rgba(255,255,255,0.25)' }}>
                        <IconPhoneCall style={{ width: fs(4.4), height: fs(4.4) }} stroke={2.4} />
                    </div>
                    <div style={{ fontSize: fs(4.7), fontWeight: 800, lineHeight: 1.1 }}>{f.label.cta}</div>
                </div>
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div style={{ margin: `0 6cqw ${fs(4.4)}`, background: '#fff', borderRadius: fs(4), padding: `${fs(2.6)} ${fs(3.4)}`, boxShadow: '0 0.9cqw 2cqw rgba(15,23,42,0.14)', textAlign: 'center' }}>
            <div className="flex items-center justify-center" style={{ gap: fs(2.2) }}>
                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(8.6), height: fs(8.6), border: `0.4cqw solid ${p.bg}`, padding: '0.6cqw' }} />}
                <div style={{ color: p.header, fontSize: fs(6), fontWeight: 800, lineHeight: 1.12, opacity: f.shop.placeholder ? 0.6 : 1 }}>{f.shop.nameShown}</div>
            </div>
            <div className="flex items-center justify-center" style={{ gap: fs(1.2), marginTop: fs(0.8), color: p.ink, fontSize: fs(3.6), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.6 : 0.9 }}>
                <IconMapPin className="flex-none" style={{ width: fs(3.8), height: fs(3.8), color: p.cal2 }} />
                <div>{f.shop.addressShown}</div>
            </div>
            {f.shop.phone && (
                <div className="flex items-center justify-center" style={{ gap: fs(1.2), marginTop: fs(0.6), color: p.ink, fontSize: fs(4.6), fontWeight: 800 }}>
                    <IconPhoneCall className="flex-none" style={{ width: fs(4.2), height: fs(4.2), color: p.cal1 }} />
                    <div>{f.shop.phone}</div>
                </div>
            )}
        </div>
    ) : null;

    return (
        <PosterSheet
            deps={[job, business, theme, showFooter, t]}
            className={className}
            fontFamily={FONT}
            background={`radial-gradient(${hexToRgba(p.ink, 0.07)} 0.18cqw, transparent 0.2cqw) 0 0 / 3.4cqw 3.4cqw, ${solid(p.bg)}`}
            body={body}
            footer={footer}
        />
    );
}
